import React from "react";
import {IconButton, ListItem} from "@material-ui/core";
import FlexRow from "view/components/Layout/FlexRow";
import FileIcon from "view/components/FileIcon";
import filesize from "filesize";
import NewTabIcon from "@material-ui/icons/OpenInNew";
import {AttachmentFile, Extension} from "ts/api/attachmentFile";
import parsePath from "parse-filepath";
import {Typography} from "@mui/material";
import TruncatedText from "view/components/TruncatedText";
import {convertFileUrl} from "tools/convertFileUrl";
import CopyFileLinkButton from "view/components/CopyFileLinkButton";
import useStyles from "./styles";

const CommentAttachment = ({
  attachment,
  allowCopyLink,
}: {
  attachment: AttachmentFile;
  /** Render the "copy file link" icon (task-domain files only). */
  allowCopyLink?: boolean;
}) => {
  const {
    title,
    file: {size, name},
    url,
    provider,
  } = attachment;
  const {ext} = parsePath(name);
  const type = ext.replace(".", "") as Extension;
  const styles = useStyles();

  const onOpen = () => {
    // Open the tab synchronously to preserve the user-gesture (else
    // popup-blockers fire after the await), then redirect once
    // convertFileUrl resolves the token-augmented URL. Don't pass
    // `noopener` — window.open returns null with it set, leaving no
    // handle to navigate.
    const popup = window.open("", "_blank");
    convertFileUrl(url, provider)
      .then((finalUrl) => {
        if (popup) popup.location.href = finalUrl;
      })
      .catch(() => {
        if (popup) popup.location.href = url;
      });
  };

  return (
    <ListItem className={styles.item}>
      <FlexRow justifyContent="space-between" fullWidth gap={2}>
        <FlexRow component="span" gap={2}>
          <FileIcon extension={type} />
          <Typography className={styles.size}>
            {type.toUpperCase()}, {filesize(size)}
          </Typography>
        </FlexRow>
        <TruncatedText
          text={title}
          maxLength={48}
          className={styles.name}
          style={{fontWeight: "bold"}}
        />

        <FlexRow component="span" alignItems="center">
          {/* Opt-in: the instance artifacts (print / certificate PDF) are
              task-domain files the share endpoint accepts. Comment
              attachments live in `task_comment_to_files`, which it does not —
              so the thread keeps rendering only the open button. */}
          {allowCopyLink && (
            <CopyFileLinkButton
              fileId={attachment.fileId ?? attachment.id}
              url={url}
              className={styles.copyIcon}
            />
          )}
          <IconButton className={styles.icon} onClick={onOpen} size="small">
            <NewTabIcon />
          </IconButton>
        </FlexRow>
      </FlexRow>
    </ListItem>
  );
};

export default CommentAttachment;
